// Reminders for one contact or deal, with the one-click "Follow up in…" quick picks.
// Each new reminder has a type (call / email / meeting; Goals spec Phase 0).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { addDaysISO, date, todayISO } from '../lib/format';
import { supabase, unwrap } from '../lib/supabase';
import { ACTIVITY, ACTIVITY_TYPES, defaultNote, type ActivityType } from '../lib/activity';
import { TypeIcon, useCheckOff } from './CheckOff';
import { useToast } from './Toast';
import { Button, cx, Empty, Input, Pill } from './ui';

const QUICK = [
  { label: '3 days', days: 3 },
  { label: '1 week', days: 7 },
  { label: '2 weeks', days: 14 },
  { label: '1 month', days: 30 },
];

export const REMINDER_TYPE_LABEL: Record<string, string> = {
  manual_follow_up: 'Follow up',
  deal_next_step: 'Deal step',
  post_close_touchpoint: 'Post-close',
  payment_chase: 'Chase payment',
  lease_expiration_touchpoint: 'Lease expiration',
};

export function useCompleteReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, done }: { id: string; done: boolean }) =>
      unwrap(await supabase.from('reminders').update({ status: done ? 'done' : 'open', completed_at: done ? new Date().toISOString() : null }).eq('id', id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reminders'] });
      qc.invalidateQueries({ queryKey: ['nav-counts'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Delete a reminder (trash can; Brett 2026-10-23), with Undo. Reminders the CRM makes from deal
 * dates would be recreated on the next deal save, so for those "delete" marks them done instead
 * (completed ones are never reopened).
 */
export function useDeleteReminder() {
  const qc = useQueryClient();
  const toast = useToast();
  const refresh = () => qc.invalidateQueries();
  return useMutation({
    mutationFn: async (id: string) => {
      const row = unwrap(await supabase.from('reminders').select('*').eq('id', id).single());
      if (row.auto_generated) unwrap(await supabase.from('reminders').update({ status: 'done', completed_at: new Date().toISOString() }).eq('id', id));
      else unwrap(await supabase.from('reminders').delete().eq('id', id));
      return row;
    },
    onSuccess: (row) => {
      refresh();
      const { search: _s, ...copy } = row as typeof row & { search?: unknown };
      toast({
        message: 'Reminder deleted.',
        autoHideMs: 8000,
        action: {
          label: 'Undo',
          onClick: () =>
            void (row.auto_generated
              ? supabase.from('reminders').update({ status: row.status, completed_at: row.completed_at }).eq('id', row.id)
              : supabase.from('reminders').insert(copy)
            ).then(refresh),
        },
      });
    },
    onError: (e) => toast({ message: `Couldn't delete: ${(e as Error).message}`, autoHideMs: 8000 }),
  });
}

export function TrashButton({ onClick, label = 'Delete reminder' }: { onClick: () => void; label?: string }) {
  return (
    <button onClick={onClick} className="shrink-0 rounded p-1 text-slate-300 hover:bg-rose-50 hover:text-rose-600" title={label} aria-label={label}>
      <Trash2 className="h-4 w-4" />
    </button>
  );
}

export function RemindersPanel({ contactId, dealId, label }: { contactId?: string; dealId?: string; label: string }) {
  const qc = useQueryClient();
  const [custom, setCustom] = useState('');
  const [note, setNote] = useState('');
  const [type, setType] = useState<ActivityType>('call');
  const key = ['reminders', contactId ?? null, dealId ?? null];

  const { data = [] } = useQuery({
    queryKey: key,
    queryFn: async () => {
      let q = supabase.from('reminders').select('id, due_date, note, reminder_type, activity_type, status, auto_generated').neq('status', 'done').order('due_date');
      q = contactId ? q.eq('contact_id', contactId) : q.eq('deal_id', dealId!);
      return unwrap(await q);
    },
  });

  const add = useMutation({
    mutationFn: async (due: string) =>
      unwrap(
        await supabase.from('reminders').insert({
          contact_id: contactId ?? null,
          deal_id: dealId ?? null,
          due_date: due,
          note: note.trim() || defaultNote(type, label),
          reminder_type: 'manual_follow_up',
          activity_type: type,
        }),
      ),
    onSuccess: () => {
      setNote('');
      setCustom('');
      qc.invalidateQueries({ queryKey: ['reminders'] });
      qc.invalidateQueries({ queryKey: ['nav-counts'] });
    },
  });
  const { checkOffReminder } = useCheckOff();
  const del = useDeleteReminder();
  const today = todayISO();

  return (
    <div className="space-y-3">
      <div className="space-y-2 rounded-md bg-slate-50 p-2.5">
        <div className="flex gap-1" role="radiogroup" aria-label="Reminder type">
          {ACTIVITY_TYPES.map((t) => {
            const A = ACTIVITY[t];
            return (
              <button
                key={t}
                role="radio"
                aria-checked={type === t}
                onClick={() => setType(t)}
                className={cx('inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium', type === t ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-indigo-300')}
              >
                <A.icon className="h-3.5 w-3.5" /> {A.label}
              </button>
            );
          })}
        </div>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={`${defaultNote(type, label)}…`} />
        <div className="flex flex-wrap items-center gap-1.5">
          {QUICK.map((q) => (
            <Button key={q.days} size="sm" onClick={() => add.mutate(addDaysISO(q.days))} disabled={add.isPending}>
              {q.label}
            </Button>
          ))}
          <Input type="date" value={custom} onChange={(e) => setCustom(e.target.value)} className="!w-auto !py-0.5 text-xs" />
          {custom && (
            <Button size="sm" variant="primary" onClick={() => add.mutate(custom)}>
              Add
            </Button>
          )}
        </div>
      </div>
      {data.length === 0 ? (
        <Empty>No open reminders.</Empty>
      ) : (
        <ul className="space-y-1.5">
          {data.map((r) => (
            <li key={r.id} className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                aria-label="Mark done"
                onChange={() => checkOffReminder({ id: r.id, activity_type: r.activity_type, contactId: contactId ?? null, dealId: dealId ?? null, name: label })}
              />
              <TypeIcon type={r.activity_type} />
              <div className="min-w-0 flex-1">
                <div className="text-slate-800">{r.note}</div>
                <div className="mt-0.5 flex items-center gap-1.5 text-xs">
                  <span className={r.due_date < today ? 'font-medium text-rose-600' : 'text-slate-500'}>{date(r.due_date)}</span>
                  <Pill>{REMINDER_TYPE_LABEL[r.reminder_type] ?? r.reminder_type}</Pill>
                </div>
              </div>
              <TrashButton onClick={() => del.mutate(r.id)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
